
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Receipt,
  MoreVertical,
  FileText,
  Plus,
  CreditCard,
  ShoppingCart,
  Pencil,
  RefreshCw,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { AddExpenseDialog, ExpenseFormData } from '@/components/finance/AddExpenseDialog';
import { ViewExpenseDialog } from '@/components/finance/ViewExpenseDialog';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { expenseService } from '@/services/expense.service';
import { useAuthStore } from '@/stores/authStore';

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return dateString;
  }
};

const formatCurrencyValue = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};


interface Expense {
  id: string;
  description: string;
  amount: number;
  date: string;
  category: string;
  paymentMethod: string;
  status: 'Pending' | 'Paid';
}

export default function Expenses() {
  const { hasSubModulePermission } = useAuthStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [expensesList, setExpensesList] = useState<Expense[]>([]);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [viewingExpense, setViewingExpense] = useState<Expense | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);
  const [allExpensesForStats, setAllExpensesForStats] = useState<Expense[]>([]);

  useEffect(() => {
    loadExpenses();
    loadStats();
  }, [currentPage, pageSize, searchTerm]);

  const loadStats = async () => {
    try {
      const response = await expenseService.getExpenses({ limit: 10000 });
      const normalized = (response.data || []).map((e: any) => ({
        id: (e.id || e._id) as string,
        description: e.description,
        category: e.category,
        amount: Number(
          e.amount ??
          e.totalAmount ??
          e.total_amount ??
          e.total ??
          e.value ??
          e.price ??
          0
        ) || 0,
        date: e.date || e.expenseDate || e.createdAt || e.updatedAt,
        paymentMethod: (e.paymentMethod || e.payment_mode || e.paymentMode || e.paymentType || e.payment_type || ''),
        status: ((() => {
          const s = (e.status || '').toLowerCase();
          if (s === 'approved') return 'Paid';
          if (s === 'pending') return 'Pending';
          if (s === 'rejected') return 'Rejected';
          return e.status as any;
        })()),
      }));
      setAllExpensesForStats(normalized);
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      setIsRefreshing(true);
      const response = await expenseService.getExpenses({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
      });
      const normalized = (response.data || []).map((e: any) => ({
        id: (e.id || e._id) as string,
        description: e.description,
        category: e.category,
        amount: Number(
          e.amount ??
          e.totalAmount ??
          e.total_amount ??
          e.total ??
          e.value ??
          e.price ??
          0
        ) || 0,
        date: e.date || e.expenseDate || e.createdAt || e.updatedAt,
        paymentMethod: (e.paymentMethod || e.payment_mode || e.paymentMode || e.paymentType || e.payment_type || ''),
        status: ((() => {
          const s = (e.status || '').toLowerCase();
          if (s === 'approved') return 'Paid';
          if (s === 'pending') return 'Pending';
          if (s === 'rejected') return 'Rejected';
          return e.status as any;
        })()),
      }));
      setExpensesList(normalized);
      setTotalPages(Math.ceil(response.total / pageSize));
      setTotalExpenses(response.total);
    } catch (error) {
      console.error('Failed to load expenses', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: string) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1);
  };

  const handleAddExpense = (data: ExpenseFormData) => {
    (async () => {
      try {
        if (editingExpense) {
          await expenseService.updateExpense(editingExpense.id, data);
          toast.success('Expense updated successfully');
          setEditingExpense(null);
        } else {
          await expenseService.createExpense(data);
          toast.success('Expense added successfully');
        }
        await loadExpenses();
        await loadStats();
        setCurrentPage(1);
      } catch (error) {
        console.error('Failed to save expense', error);
        toast.error('Failed to save expense');
      }
    })();
  };

  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchTerm('');
    await loadExpenses();
    await loadStats();
    toast.success('Data refreshed successfully');
  };

  const calculateStats = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const totalAmount = allExpensesForStats.reduce((sum, exp) => sum + exp.amount, 0);
    const pendingExpenses = allExpensesForStats.filter(exp => exp.status === 'Pending');
    const pendingAmount = pendingExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    
    const thisMonthExpenses = allExpensesForStats.filter(exp => {
      if (!exp.date) return false;
      const expDate = new Date(exp.date);
      return expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear;
    });
    const thisMonthAmount = thisMonthExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    
    const averageExpense = allExpensesForStats.length > 0 
      ? totalAmount / allExpensesForStats.length 
      : 0;
    
    return {
      totalAmount,
      pendingAmount,
      pendingCount: pendingExpenses.length,
      thisMonthAmount,
      averageExpense,
    };
  };

  const stats = calculateStats();

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingExpense(null);
    }
  };

  const handleViewExpense = (expense: Expense) => {
    setViewingExpense(expense);
    setIsViewDialogOpen(true);
  };

  const openDeleteExpense = (expense: Expense) => {
    setExpenseToDelete(expense);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    (async () => {
      try {
        if (expenseToDelete) {
          await expenseService.deleteExpense(expenseToDelete.id);
          toast.success('Expense deleted successfully');
        }
        await loadExpenses();
        await loadStats();
      } catch (error) {
        console.error('Failed to delete expense', error);
        toast.error('Failed to delete expense');
      } finally {
        setDeleteDialogOpen(false);
        setExpenseToDelete(null);
      }
    })();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Expenses</h1>
          <p className="text-muted-foreground">
            Track and manage your business expenses
          </p>
        </div>
        {hasSubModulePermission('finance', 'expense', 'create') && (
          <Button onClick={() => setIsDialogOpen(true)} className="rounded-lg">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrencyValue(stats.totalAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {allExpensesForStats.length} total expenses
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Expenses</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrencyValue(stats.pendingAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.pendingCount} expense{stats.pendingCount !== 1 ? 's' : ''} pending
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrencyValue(stats.thisMonthAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              Current month expenses
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Expense</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrencyValue(stats.averageExpense)}
            </div>
            <p className="text-xs text-muted-foreground">
              Per expense
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Expenses</CardTitle>
              <CardDescription>View and manage your expenses</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search expenses..." 
                  className="pl-8" 
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <Select
                value={pageSize.toString()}
                onValueChange={handlePageSizeChange}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Rows per page" />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size} rows
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      className="rounded-md"
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${
                          isRefreshing ? "animate-spin" : ""
                        }`}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Refresh Data</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isRefreshing ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center space-y-2">
                        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">
                          Loading expenses...
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : expensesList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center space-y-2">
                        <span className="text-sm text-muted-foreground">
                          No expenses found
                        </span>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSearchTerm("");
                          }}
                          className="rounded-md"
                        >
                          Clear Filters
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  expensesList.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell className="font-medium">{expense.description}</TableCell>
                  <TableCell>{expense.category}</TableCell>
                  <TableCell>{formatCurrencyValue(expense.amount)}</TableCell>
                  <TableCell>{formatDate(expense.date)}</TableCell>
                  <TableCell>{expense.paymentMethod}</TableCell>
                  <TableCell>
                    <Badge
                      variant={expense.status === 'Paid' ? 'default' : 'secondary'}
                    >
                      {expense.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewExpense(expense)}>
                          <FileText className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {hasSubModulePermission('finance', 'expense', 'edit') && (
                          <DropdownMenuItem onClick={() => handleEditExpense(expense)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {hasSubModulePermission('finance', 'expense', 'delete') && (
                          <DropdownMenuItem onClick={() => openDeleteExpense(expense)} className="text-red-600 focus:text-red-600">
                            <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {!loading && !isRefreshing && expensesList.length > 0 && (
            <div className="flex items-center justify-between mt-4 px-4">
              <div className="text-sm text-gray-500">
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, totalExpenses)} of{" "}
                {totalExpenses} entries
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="rounded-md"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {(() => {
                    const pageButtons: JSX.Element[] = [];
                    
                    if (totalPages > 3) {
                      let startPage = Math.max(1, currentPage - 1);
                      let endPage = Math.min(totalPages, currentPage + 1);
                      
                      if (startPage === 1) {
                        endPage = Math.min(totalPages, 3);
                      }
                      if (endPage === totalPages) {
                        startPage = Math.max(1, totalPages - 2);
                      }
                      
                      if (startPage > 1) {
                        pageButtons.push(
                          <Button
                            key={1}
                            variant={1 === currentPage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePageChange(1)}
                            className={`rounded-md ${
                              1 === currentPage
                                ? 'bg-[#000000] hover:bg-[#000000] text-white'
                                : ''
                            }`}
                          >
                            1
                          </Button>
                        );
                        if (startPage > 2) {
                          pageButtons.push(
                            <span key="ellipsis-start" className="px-2 text-muted-foreground">
                              ..
                            </span>
                          );
                        }
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pageButtons.push(
                          <Button
                            key={i}
                            variant={i === currentPage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePageChange(i)}
                            className={`rounded-md ${
                              i === currentPage
                                ? 'bg-[#000000] hover:bg-[#000000] text-white'
                                : ''
                            }`}
                          >
                            {i}
                          </Button>
                        );
                      }
                      
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pageButtons.push(
                            <span key="ellipsis-end" className="px-2 text-muted-foreground">
                              ..
                            </span>
                          );
                        }
                        pageButtons.push(
                          <Button
                            key={totalPages}
                            variant={totalPages === currentPage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            className={`rounded-md ${
                              totalPages === currentPage
                                ? 'bg-[#000000] hover:bg-[#000000] text-white'
                                : ''
                            }`}
                          >
                            {totalPages}
                          </Button>
                        );
                      }
                    } else {
                      for (let i = 1; i <= totalPages; i++) {
                        pageButtons.push(
                          <Button
                            key={i}
                            variant={i === currentPage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePageChange(i)}
                            className={`rounded-md ${
                              i === currentPage
                                ? 'bg-[#000000] hover:bg-[#000000] text-white'
                                : ''
                            }`}
                          >
                            {i}
                          </Button>
                        );
                      }
                    }
                    
                    return pageButtons;
                  })()}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="rounded-md"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AddExpenseDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        onSubmit={handleAddExpense}
        initialData={editingExpense ? {
          description: editingExpense.description,
          category: editingExpense.category,
          amount: editingExpense.amount,
          date: editingExpense.date,
          paymentMethod: editingExpense.paymentMethod,
          status: editingExpense.status,
        } : undefined}
      />

      <ViewExpenseDialog
        expense={viewingExpense}
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{expenseToDelete?.description}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={handleConfirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 